// Satu tempat untuk kalkulasi "hari ini" / batas hari WIB (Asia/Jakarta,
// UTC+7, tanpa DST). JANGAN pakai `new Date().getFullYear()/getMonth()/
// getDate()` untuk kebutuhan begini — itu memakai timezone LOKAL PROSES,
// yang bisa berarti browser ATAU server Node (Next.js App Router men-SSR
// Client Component sekali di server sebelum hydration), bukan WIB. Semua
// fungsi di sini eksplisit ke Asia/Jakarta lewat `Intl.DateTimeFormat`,
// jadi hasilnya sama persis apa pun TZ proses yang menjalankannya
// (bughunt I2-zona-waktu).

export const WIB_TIME_ZONE = "Asia/Jakarta";
const WIB_OFFSET = "+07:00";
const MS_PER_DAY = 86_400_000;

/**
 * Tanggal hari ini di Asia/Jakarta sebagai 'yyyy-mm-dd'.
 *
 * Dibangun dari `formatToParts` alih-alih merangkai string lokal: hasilnya
 * tidak bergantung pada urutan/pemisah yang dipilih implementasi Intl.
 */
export function todayWIB(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: WIB_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const pick = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${pick("year")}-${pick("month")}-${pick("day")}`;
}

/**
 * `yyyy-mm-dd` → ISO dengan OFFSET WIB EKSPLISIT (`+07:00`), sehingga
 * `::timestamptz` di Postgres / `new Date(...)` di JS menafsirkannya sebagai
 * instan WIB yang benar — bukan UTC (bughunt F3-4: tanpa offset, sesi
 * PostgREST yang berjalan UTC menyimpan "1 Apr 23:59:59" sebagai 2 Apr
 * 06:59:59 WIB).
 *
 * `ujung = "mulai"` → awal hari (00:00:00); `"akhir"` → detik terakhir hari
 * itu (23:59:59). Input yang bukan tanggal (termasuk string kosong) → `null`.
 */
export function wibDateBoundaryIso(
  tanggal: string | null | undefined,
  ujung: "mulai" | "akhir"
): string | null {
  const hari = (tanggal ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(hari)) return null;
  const jam = ujung === "mulai" ? "00:00:00" : "23:59:59";
  return `${hari}T${jam}${WIB_OFFSET}`;
}

/**
 * Instan ISO apa pun → 'yyyy-mm-dd' di kalender WIB, untuk `<input type="date">`.
 * Pasangan balik `wibDateBoundaryIso`: timestamptz dari PostgREST datang dalam
 * UTC, jadi `iso.slice(0, 10)` membaca awal hari WIB (00:00 +07 = 17:00Z hari
 * sebelumnya) sebagai tanggal KEMARIN. Kosong / bukan tanggal → "".
 */
export function isoToWibYmd(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : todayWIB(d);
}

/** Tengah malam WIB suatu 'yyyy-mm-dd' → ISO (instan absolut, UTC-Z). */
export function wibMidnightIso(ymd: string): string {
  return new Date(`${ymd}T00:00:00${WIB_OFFSET}`).toISOString();
}

/**
 * Geser 'yyyy-mm-dd' sebanyak N hari KALENDER WIB (boleh negatif).
 * Aman lewat aritmetika ms murni: WIB adalah offset tetap tanpa DST, jadi
 * setiap hari kalendernya persis 86.400.000 ms.
 */
export function shiftYmdWIB(ymd: string, days: number): string {
  const instant = new Date(`${ymd}T00:00:00${WIB_OFFSET}`).getTime() + days * MS_PER_DAY;
  return todayWIB(new Date(instant));
}

export type WibRange = { start: string; end: string };

/**
 * [tengah malam WIB `days-1` hari lalu, tengah malam WIB besok) — `days=1`
 * berarti "hari ini saja".
 */
export function wibLastNDaysRange(days: number, ref: Date = new Date()): WibRange {
  const today = todayWIB(ref);
  return {
    start: wibMidnightIso(shiftYmdWIB(today, -(days - 1))),
    end: wibMidnightIso(shiftYmdWIB(today, 1)),
  };
}

/**
 * [1 awal bulan WIB, 1 awal bulan berikutnya). `monthOffset` menggeser bulan
 * acuan (mis. -1 = bulan lalu).
 */
export function wibMonthRange(ref: Date = new Date(), monthOffset = 0): WibRange {
  const [y, m] = todayWIB(ref).split("-").map(Number);
  const zeroBased = m - 1 + monthOffset;
  const ymdOf = (total: number) => {
    const year = y + Math.floor(total / 12);
    const month0 = ((total % 12) + 12) % 12;
    return `${year}-${String(month0 + 1).padStart(2, "0")}-01`;
  };
  return { start: wibMidnightIso(ymdOf(zeroBased)), end: wibMidnightIso(ymdOf(zeroBased + 1)) };
}

/** "YYYY-MM" bulan kalender WIB saat `ref` (bawaan: sekarang). */
export function wibMonthKey(ref: Date = new Date()): string {
  return todayWIB(ref).slice(0, 7);
}

const MONTH_KEY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Rentang bulan kalender WIB untuk kunci "YYYY-MM": [tgl 1 00:00 WIB, tgl 1
 * bulan berikutnya 00:00 WIB). Pasangan WIB dari `monthKeyRange`
 * (expenseBudget.ts, sengaja lokal) — dipakai layar yang memilah instan
 * `created_at` per bulan, mis. riwayat /tagihan. Kunci tak sah → bulan WIB
 * berjalan saat `ref` (cermin fallback `monthKeyRange`/`customRange`).
 */
export function wibMonthKeyRange(month: string, ref: Date = new Date()): WibRange {
  const key = (month ?? "").trim();
  // Tengah malam WIB tgl 1 jatuh di bulan itu sendiri menurut `todayWIB`,
  // jadi aritmetika bulannya cukup dipinjam dari `wibMonthRange` yang teruji.
  return MONTH_KEY_RE.test(key)
    ? wibMonthRange(new Date(wibMidnightIso(`${key}-01`)))
    : wibMonthRange(ref);
}

/**
 * Apakah instan `iso` jatuh di [start, end)? Dibanding sebagai WAKTU
 * (`Date.parse`), bukan string: `created_at` dari PostgREST berbentuk
 * "…T17:00:00+00:00" sedangkan batas rentang (`toISOString`) "…T17:00:00.000Z"
 * — dibanding sebagai string, instan yang tepat di batas terlempar keluar
 * karena '+' < '.'. Instan tak terbaca → false.
 */
export function isInstantInRange(iso: string, range: WibRange): boolean {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  return t >= Date.parse(range.start) && t < Date.parse(range.end);
}
